import { useCallback, useEffect } from "react";

/**
 * Google reCAPTCHA v3（スコアベース）用フック（§4.1）
 *
 * - マウント時に reCAPTCHA v3 スクリプトを動的ロードする
 *   （サイトキーは VITE_RECAPTCHA_SITE_KEY から取得）。
 * - executeRecaptcha(action) でトークンを取得し、ログイン API へ渡す。
 * - サイトキー未設定時は何もしない（executeRecaptcha は undefined を返す）。
 *   ローカル開発でキー無しでもログインできるようにするため
 *   （BE 側も RECAPTCHA_SECRET_KEY 未設定なら検証スキップ）。
 *
 * v3 はチャレンジ UI を出さず、右下にバッジを表示する。
 */

const SCRIPT_ID = "recaptcha-v3-script";
const scriptLoadPromises = new Map<string, Promise<void>>();

interface Grecaptcha {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

function getSiteKey(): string {
  // shared-auth は複数の Vite アプリから利用される。import.meta.env は
  // 各アプリのビルド時に解決される。型は環境非依存にするため any 経由で読む。
  const env = (import.meta as unknown as { env?: Record<string, string> }).env;
  return env?.VITE_RECAPTCHA_SITE_KEY ?? "";
}

function loadRecaptchaScript(siteKey: string): Promise<void> {
  if (window.grecaptcha) return Promise.resolve();

  const pendingLoad = scriptLoadPromises.get(siteKey);
  if (pendingLoad) return pendingLoad;

  const existingScript = document.getElementById(
    SCRIPT_ID
  ) as HTMLScriptElement | null;
  const script = existingScript ?? document.createElement("script");

  const loadPromise = new Promise<void>((resolve, reject) => {
    const handleLoad = () => {
      if (window.grecaptcha) {
        resolve();
        return;
      }

      script.remove();
      reject(new Error("reCAPTCHA script loaded without its API"));
    };

    const handleError = () => {
      script.remove();
      reject(new Error("reCAPTCHA script failed to load"));
    };

    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", handleError, { once: true });

    if (!existingScript) {
      script.id = SCRIPT_ID;
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(
        siteKey
      )}`;
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
  });

  const trackedLoad = loadPromise.finally(() => {
    scriptLoadPromises.delete(siteKey);
  });
  scriptLoadPromises.set(siteKey, trackedLoad);
  return trackedLoad;
}

export async function executeRecaptchaToken(
  siteKey: string,
  action: string
): Promise<string | undefined> {
  if (!siteKey) return undefined;

  try {
    await loadRecaptchaScript(siteKey);
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha) return undefined;

    return await new Promise<string>((resolve, reject) => {
      grecaptcha.ready(() => {
        grecaptcha.execute(siteKey, { action }).then(resolve).catch(reject);
      });
    });
  } catch (error) {
    console.error("[useRecaptcha] execute failed:", error);
    return undefined;
  }
}

export function useRecaptcha() {
  const siteKey = getSiteKey();

  useEffect(() => {
    if (!siteKey) return; // キー未設定なら何もしない（検証スキップ）
    void loadRecaptchaScript(siteKey).catch((error) => {
      console.error("[useRecaptcha] script load failed:", error);
    });
  }, [siteKey]);

  /**
   * reCAPTCHA トークンを取得する。
   * @param action - action 名（BE の期待値と一致させる。ログインは "login"）
   * @returns トークン文字列。キー未設定 or 取得失敗時は undefined
   */
  const executeRecaptcha = useCallback(
    (action: string): Promise<string | undefined> =>
      executeRecaptchaToken(siteKey, action),
    [siteKey]
  );

  return { executeRecaptcha };
}
